import { defineMessages } from '@edx/frontend-platform/i18n';

const messages = defineMessages({
  heading: {
    id: 'liveSessions.heading',
    defaultMessage: 'Live Sessions',
    description: 'Heading for the live sessions page',
  },
  searchPlaceholder: {
    id: 'liveSessions.searchPlaceholder',
    defaultMessage: 'Search live sessions',
    description: 'Placeholder for the live sessions search input',
  },
  searchLabel: {
    id: 'liveSessions.searchLabel',
    defaultMessage: 'Search',
    description: 'Screen reader label for the live sessions search field',
  },
  searchSubmit: {
    id: 'liveSessions.searchSubmit',
    defaultMessage: 'Submit',
    description: 'Screen reader label for the live sessions search submit button',
  },
  searchClear: {
    id: 'liveSessions.searchClear',
    defaultMessage: 'Clear',
    description: 'Screen reader label for the live sessions search clear button',
  },
  loading: {
    id: 'liveSessions.loading',
    defaultMessage: 'Loading live sessions',
    description: 'Screen reader text shown while live sessions are loading',
  },
  loadError: {
    id: 'liveSessions.loadError',
    defaultMessage: 'Error loading live sessions.',
    description: 'Error message shown when live sessions fail to load',
  },
  noSessionsFound: {
    id: 'liveSessions.noSessionsFound',
    defaultMessage: 'No live sessions found.',
    description: 'Message shown when no live sessions match the current filters',
  },
  emptyStateTitle: {
    id: 'liveSessions.emptyStateTitle',
    defaultMessage: 'No lives scheduled',
    description: 'Title for the live sessions empty state shown when the learner has no live sessions at all',
  },
  emptyStateSubtitle: {
    id: 'liveSessions.emptyStateSubtitle',
    defaultMessage: 'Live sessions will appear here once your instructor schedules one',
    description: 'Subtitle for the live sessions empty state shown when the learner has no live sessions at all',
  },
  emptyStateImageAlt: {
    id: 'liveSessions.emptyStateImageAlt',
    defaultMessage: 'No live sessions illustration',
    description: 'Alt text for the live sessions empty state illustration',
  },
  paginationLabel: {
    id: 'liveSessions.paginationLabel',
    defaultMessage: 'live sessions pagination',
    description: 'Accessible label for the live sessions pagination control',
  },
  statusLive: {
    id: 'liveSessions.statusLive',
    defaultMessage: 'Live',
    description: 'Status label shown on live session cards when the session is currently live',
  },
  statusEnded: {
    id: 'liveSessions.statusEnded',
    defaultMessage: 'Live ended',
    description: 'Status label shown on live session cards when the session has ended',
  },
  statusScheduled: {
    id: 'liveSessions.statusScheduled',
    defaultMessage: 'Scheduled',
    description: 'Status label shown on live session cards when the session is scheduled',
  },
  liveBadge: {
    id: 'liveSessions.liveBadge',
    defaultMessage: 'live',
    description: 'Badge label shown on the live session card',
  },
  detailLoading: {
    id: 'liveSessions.detail.loading',
    defaultMessage: 'Loading live session',
    description: 'Screen reader text shown while a live session detail page is loading',
  },
  detailLoadError: {
    id: 'liveSessions.detail.loadError',
    defaultMessage: 'Error loading live session.',
    description: 'Error message shown when a live session detail fails to load',
  },
  detailNotFound: {
    id: 'liveSessions.detail.notFound',
    defaultMessage: 'Live session not found.',
    description: 'Message shown when no live session matches the URL',
  },
  joinUrlUnavailable: {
    id: 'liveSessions.detail.joinUrlUnavailable',
    defaultMessage: 'Could not get join URL.',
    description: 'Error shown when the join URL could not be retrieved for a live session',
  },
  joinSessionFailed: {
    id: 'liveSessions.detail.joinSessionFailed',
    defaultMessage: 'Could not join session.',
    description: 'Error shown when joining a live session fails',
  },
  openSessionFailed: {
    id: 'liveSessions.detail.openSessionFailed',
    defaultMessage: 'Could not open session.',
    description: 'Error shown when opening a live session in a new tab fails',
  },
  backToLiveSessions: {
    id: 'liveSessions.detail.back',
    defaultMessage: 'Back to Live Sessions',
    description: 'Back navigation link to the live sessions list',
  },
  liveSessionFrameTitle: {
    id: 'liveSessions.detail.frameTitle',
    defaultMessage: 'Live session',
    description: 'Title attribute for the embedded live session iframe',
  },
  openInNewTab: {
    id: 'liveSessions.detail.openInNewTab',
    defaultMessage: 'Open in new tab',
    description: 'Button label to open a recording in a new browser tab',
  },
  openLiveInNewTab: {
    id: 'liveSessions.detail.openLiveInNewTab',
    defaultMessage: 'Open live session in new tab',
    description: 'Accessible label for the hero play button when a live session is active',
  },
  playRecording: {
    id: 'liveSessions.detail.playRecording',
    defaultMessage: 'Play recording',
    description: 'Accessible label for the hero play button when a recording is available',
  },
  copyLink: {
    id: 'liveSessions.detail.copyLink',
    defaultMessage: 'Copy link',
    description: 'Accessible label for the copy-link button on a live session recording card',
  },
  recordingFallbackTitle: {
    id: 'liveSessions.detail.recordingFallbackTitle',
    defaultMessage: 'Recording {index}',
    description: 'Fallback title for a recording card when the video has no title',
  },
  aboutSession: {
    id: 'liveSessions.detail.aboutSession',
    defaultMessage: 'About this session',
    description: 'Heading for the about-this-session card on the live session detail page',
  },
  sessionDetails: {
    id: 'liveSessions.detail.sessionDetails',
    defaultMessage: 'Session details',
    description: 'Heading for the session details card on the live session detail page',
  },
  scheduledDateLabel: {
    id: 'liveSessions.detail.scheduledDate',
    defaultMessage: 'Scheduled date',
    description: 'Label for the scheduled date row on the live session detail page',
  },
  scheduledTimeLabel: {
    id: 'liveSessions.detail.scheduledTime',
    defaultMessage: 'Scheduled time',
    description: 'Label for the scheduled time row on the live session detail page',
  },
  statusLabel: {
    id: 'liveSessions.detail.statusLabel',
    defaultMessage: 'Status',
    description: 'Label for the status row on the live session detail page',
  },
  estimatedDurationLabel: {
    id: 'liveSessions.detail.estimatedDuration',
    defaultMessage: 'Estimated duration',
    description: 'Label for the estimated duration row on the live session detail page',
  },
  providerLabel: {
    id: 'liveSessions.detail.providerLabel',
    defaultMessage: 'Provider',
    description: 'Label for the provider row on the live session detail page',
  },
  recordingsHeading: {
    id: 'liveSessions.detail.recordingsHeading',
    defaultMessage: 'Recordings',
    description: 'Heading for the recordings section on the live session detail page',
  },
  recordingsAvailable: {
    id: 'liveSessions.detail.recordingsAvailable',
    defaultMessage: '{count, plural, one {# recording} other {# recordings}} available from this session.',
    description: 'Subtitle indicating how many recordings are available for a live session',
  },
  noRecordings: {
    id: 'liveSessions.detail.noRecordings',
    defaultMessage: 'No recordings are available for this session.',
    description: 'Empty state shown when a live session has ended but has no recordings',
  },
  partOfCourse: {
    id: 'liveSessions.detail.partOfCourse',
    defaultMessage: 'Part of a course',
    description: 'Heading for the related-course section on the live session detail page',
  },
  partOfCourseDescription: {
    id: 'liveSessions.detail.partOfCourseDescription',
    defaultMessage: 'This live session is included as part of the following course:',
    description: 'Description text introducing the related course on the live session detail page',
  },
  courseBadgeLabel: {
    id: 'liveSessions.detail.courseBadgeLabel',
    defaultMessage: 'COURSE',
    description: 'Badge text shown on the related course image on the live session detail page',
  },
  startsOn: {
    id: 'liveSessions.detail.startsOn',
    defaultMessage: 'Starts {date}',
    description: 'Label indicating when the related course starts on the live session detail page',
  },
  durationMinutes: {
    id: 'liveSessions.detail.durationMinutes',
    defaultMessage: '{minutes} minutes',
    description: 'Duration shown in minutes on the live session detail page',
  },
  durationHours: {
    id: 'liveSessions.detail.durationHours',
    defaultMessage: '{hours, plural, one {# hour} other {# hours}}',
    description: 'Duration shown in whole hours on the live session detail page',
  },
  durationHoursMinutes: {
    id: 'liveSessions.detail.durationHoursMinutes',
    defaultMessage: '{hours, plural, one {# hour} other {# hours}} {minutes} minutes',
    description: 'Duration shown in hours and minutes on the live session detail page',
  },
});

export default messages;
